import React, { useState } from 'react';
import * as THREE from 'three';

export default function MaterialEditor({
  targetObject,
  modelGroup,
  disabled = false,
  compact = false,
  onUpdate
}) {
  const [materialType, setMaterialType] = useState('standard');
  const [color, setColor] = useState('#9bbcff');
  const [metalness, setMetalness] = useState(0.2);
  const [roughness, setRoughness] = useState(0.6);
  const [opacity, setOpacity] = useState(1.0);
  const [transparent, setTransparent] = useState(false);

  const applyMaterial = () => {
    const target = targetObject || modelGroup;
    if (!target || disabled) return;

    const colorValue = new THREE.Color(color);
    
    target.traverse((obj) => {
      if (obj.isMesh) {
        let newMaterial;
        
        switch (materialType) {
          case 'standard':
            newMaterial = new THREE.MeshStandardMaterial({
              color: colorValue,
              metalness,
              roughness,
              opacity,
              transparent,
              side: THREE.DoubleSide
            });
            break;
          case 'physical':
            newMaterial = new THREE.MeshPhysicalMaterial({
              color: colorValue,
              metalness,
              roughness,
              opacity,
              transparent,
              clearcoat: 0.5,
              clearcoatRoughness: 0.1,
              side: THREE.DoubleSide
            });
            break;
          case 'basic':
            newMaterial = new THREE.MeshBasicMaterial({
              color: colorValue,
              opacity,
              transparent,
              side: THREE.DoubleSide
            });
            break;
          case 'lambert':
            newMaterial = new THREE.MeshLambertMaterial({
              color: colorValue,
              opacity,
              transparent,
              side: THREE.DoubleSide
            });
            break;
          case 'phong':
            newMaterial = new THREE.MeshPhongMaterial({
              color: colorValue,
              opacity,
              transparent,
              shininess: 30,
              side: THREE.DoubleSide
            });
            break;
          default:
            return;
        }

        // Dispose old material
        if (Array.isArray(obj.material)) {
          obj.material.forEach(m => m.dispose());
        } else {
          obj.material?.dispose();
        }

        obj.material = newMaterial;
      }
    });

    if (onUpdate) onUpdate();
  };

  return (
    <div className={compact ? 'material-editor' : 'panel'}>
      <div className="panel-header">Material Editor</div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-neutral-400 mb-1 block">Material Type</label>
          <select
            value={materialType}
            onChange={(e) => setMaterialType(e.target.value)}
            className="input w-full"
          >
            <option value="standard">Standard (PBR)</option>
            <option value="physical">Physical (Advanced PBR)</option>
            <option value="basic">Basic (Unlit)</option>
            <option value="lambert">Lambert (Diffuse)</option>
            <option value="phong">Phong (Specular)</option>
          </select>
        </div>

        <div>
          <label className="text-xs text-neutral-400 mb-1 block">Color</label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-full h-10"
          />
        </div>

        {(materialType === 'standard' || materialType === 'physical') && (
          <>
            <div>
              <label className="text-xs text-neutral-400 mb-1 block">
                Metalness: {metalness.toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={metalness}
                onChange={(e) => setMetalness(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <label className="text-xs text-neutral-400 mb-1 block">
                Roughness: {roughness.toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={roughness}
                onChange={(e) => setRoughness(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          </>
        )}

        <div>
          <label className="flex items-center gap-2 text-xs text-neutral-400 mb-2">
            <input
              type="checkbox"
              checked={transparent}
              onChange={(e) => setTransparent(e.target.checked)}
            />
            Enable Transparency
          </label>

          {transparent && (
            <div className="fade-in">
              <label className="text-xs text-neutral-400 mb-1 block">
                Opacity: {opacity.toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={opacity}
                onChange={(e) => setOpacity(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          )}
        </div>

        <button
          onClick={applyMaterial}
          disabled={disabled}
          className={`btn w-full with-tooltip ${disabled ? 'btn-disabled' : 'btn-primary'}`}
          data-tooltip={disabled ? 'Select an object to edit its material' : 'Apply the selected material settings'}
        >
          Apply Material
        </button>
      </div>
    </div>
  );
}

